import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useParams } from 'react-router-dom'

import classnames from 'classnames'
import { PremiumProductOptionsSelect } from 'docReviewDashboard'
import { Formik } from 'formik'

import PagedList from 'core/application/commons/pagedList'
import getRecurringExpensesForReview, {
  RecurringExpenseForReview,
} from 'core/application/expenses/getRecurringExpensesForReview'
import lockPremium from 'core/application/expenses/lockPremium'

import { Avatar, Col, DefaultSelect, Icon, Row, Skeleton, Tooltip } from 'common/components'
import { formatDateTimeAMPM } from 'common/dateFormatters'
import { useCommandRequestV2, useLoginUserV2, useQueryRequestV2 } from 'common/hooks'

import './styles.less'

export type MoveToNextPremium = {
  currentRecurringExpenseId: string
  removeCurrentRecurringExpenseIdFromQueue: boolean
}

type PremiumsQueueProps = {
  onPremiumSelected?: (id: string) => void
  moveToNextPremium?: MoveToNextPremium
}

const PremiumsQueue = (props: PremiumsQueueProps) => {
  const { t: translate } = useTranslation()
  const { id } = useParams<any>()
  const { user } = useLoginUserV2()

  const [currentQueueNumber, setCurrentQueueNumber] = useState<number>(1)
  const [hasMoreQueueItems, setHasMoreQueueItems] = useState<boolean>(false)
  const [filter, setFilter] = useState<{ productId: number }>({ productId: -1 })
  const [activeItemId, setActiveItemId] = useState<string>('')
  const { sendCommand: sendLockCommand } = useCommandRequestV2((id: string) => lockPremium(id))
  const parentScrollDiv = useRef(null)
  const [pagedPremiums, setPagedPremiums] = useState<PagedList<RecurringExpenseForReview> | null>(null)
  const { isLoading: isPagedPremiumsLoading, sendQuery: sendGetPagedPremiums } = useQueryRequestV2({
    initialState: null,
  })

  useEffect(() => {
    ;(async () => {
      const result = await sendGetPagedPremiums(() =>
        getRecurringExpensesForReview(1, filter.productId !== -1 ? filter.productId : null),
      )
      setPagedPremiums(result)
      setHasMoreQueueItems(result.pageCount > currentQueueNumber)

      if (result.items.length > 0) {
        setActiveItemId(result.items[0].recurringExpenseId)
      }
    })()
  }, [filter])

  useEffect(() => {
    ;(async () => {
      if (activeItemId) {
        if (!isPremiumIsLocked(pagedPremiums.items, activeItemId)) {
          const result = await sendLockCommand(activeItemId)
          if (result.completed) {
            markAsLocked(activeItemId)
          }
        }

        if (props.onPremiumSelected) {
          props.onPremiumSelected(activeItemId)
        }
      } else {
        props.onPremiumSelected(null)
      }
    })()
  }, [activeItemId])

  useEffect(() => {
    if (props.moveToNextPremium) {
      const currentIndex = pagedPremiums.items.findIndex((x) => x.recurringExpenseId === activeItemId)

      if (props.moveToNextPremium.removeCurrentRecurringExpenseIdFromQueue) {
        pagedPremiums.items = pagedPremiums.items.filter(
          (x) => x.recurringExpenseId !== props.moveToNextPremium.currentRecurringExpenseId,
        )
        pagedPremiums.totalItemCount -= 1

        if (pagedPremiums.items.length === 0) {
          props.onPremiumSelected(null)
        }

        setPagedPremiums({
          ...pagedPremiums,
        })

        if (pagedPremiums.items.length === 0) {
          setActiveItemId(null)
        } else if (currentIndex < pagedPremiums.items.length) {
          setActiveItemId(pagedPremiums.items[currentIndex].recurringExpenseId)
        }
      } else {
        markAsUnLocked(activeItemId)

        if (currentIndex + 1 < pagedPremiums.items.length) {
          setActiveItemId(pagedPremiums.items[currentIndex + 1].recurringExpenseId)
        }
      }
    }
  }, [props.moveToNextPremium])

  const markAsLocked = (recurringExpenseId: string) => {
    const currentIndex = pagedPremiums.items.findIndex((x) => x.recurringExpenseId === recurringExpenseId)

    pagedPremiums.items[currentIndex].lockedBy = {
      id: user.companyUserId,
      name: `${user.firstName} ${user.lastName}`,
    }

    setPagedPremiums({
      ...pagedPremiums,
    })
  }

  const markAsUnLocked = (recurringExpenseId: string) => {
    const currentIndex = pagedPremiums.items.findIndex((x) => x.recurringExpenseId === recurringExpenseId)

    pagedPremiums.items[currentIndex].lockedBy = null

    setPagedPremiums({
      ...pagedPremiums,
    })
  }

  const onQueueEndReach = async () => {
    if (!pagedPremiums) return

    if (pagedPremiums.pageCount < currentQueueNumber) return

    const nextPageNumber = currentQueueNumber + 1
    const result = await sendGetPagedPremiums(() =>
      getRecurringExpensesForReview(nextPageNumber, filter.productId !== -1 ? filter.productId : null),
    )

    setPagedPremiums({
      ...result,
      items: [...pagedPremiums.items, ...result.items],
    })
    setCurrentQueueNumber(nextPageNumber)
    setHasMoreQueueItems(result.pageCount > nextPageNumber)
  }

  const onSinglePremiumClicked = (item: RecurringExpenseForReview) => {
    setActiveItemId(item.recurringExpenseId)
  }

  return (
    <>
      <Row className={'expense_queue__header mr-24'} align={'middle'}>
        <Col xs={24} xxl={12}>
          <h4>Premiums({pagedPremiums?.totalItemCount})</h4>
        </Col>

        <Col xs={24} xxl={12}>
          <Formik
            onSubmit={() => {}}
            initialValues={{
              productId: -1,
            }}
          >
            <DefaultSelect
              name={'productId'}
              items={PremiumProductOptionsSelect}
              onChange={(value) => {
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  productId: value,
                }))
              }}
            />
          </Formik>
        </Col>
      </Row>
      <Skeleton loading={isPagedPremiumsLoading}>
        {pagedPremiums && (
          <>
            <div className={'expense_queue mr-24'} ref={parentScrollDiv}>
              <InfiniteScroll
                pageStart={0}
                loadMore={() => onQueueEndReach()}
                hasMore={hasMoreQueueItems}
                useWindow={false}
                loader={
                  isPagedPremiumsLoading ? (
                    <div className="loader" key={0}>
                      {translate('general.loading')}
                    </div>
                  ) : (
                    <></>
                  )
                }
                getScrollParent={() => parentScrollDiv.current}
              >
                <>
                  {pagedPremiums.items.map((item: RecurringExpenseForReview) => (
                    <SinglePremium
                      item={item}
                      active={activeItemId === item.recurringExpenseId}
                      onClick={onSinglePremiumClicked}
                    />
                  ))}
                </>
              </InfiniteScroll>
            </div>
          </>
        )}
      </Skeleton>
    </>
  )
}

type SinglePremiumProps = {
  item: RecurringExpenseForReview
  active: boolean
  onClick?: (item: RecurringExpenseForReview) => void
}
const SinglePremium = (props: SinglePremiumProps) => {
  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()

  const lockIcon = props.item.lockedBy && (
    <Tooltip
      title={
        props.item.lockedBy && props.item.lockedBy.id === user.userId
          ? translate('doc-review-dashboard.expenses.review-expense-form.locked-by-me-message')
          : translate('doc-review-dashboard.expenses.review-expense-form.locked-by-other-message')
      }
      position="top"
    >
      <Icon type="far fa-lock" fontAwesome className="color-red-6" />
      {` `}
    </Tooltip>
  )

  const activeQueueItem = classnames({ expense_queue__item__active: props.active })

  return (
    <div
      className={`${activeQueueItem} expense_queue__item cursor_pointer`}
      onClick={() => {
        if (props.onClick) {
          props.onClick(props.item)
        }
      }}
    >
      <div className="flex__space_between">
        <p>
          {lockIcon} {props.item.companyUser.name}
        </p>
        <p>${props.item.requestedAmount}</p>
      </div>
      <div>
        <p>#{props.item.expenseNumber}</p>
      </div>
      <div className="flex__space_between flex__space_between_centered">
        <span className="color-gray-7">{formatDateTimeAMPM(props.item.dateSubmitted)}</span>

        {props.item.lockedBy && (
          <Tooltip title={props.item.lockedBy.name} position="top">
            <Avatar shape="circle" size="default" text={props.item.lockedBy.name[0]} className="pk--header-user-icon" />
          </Tooltip>
        )}
      </div>

      {props.item.resubmitted && (
        <div className="flex__space_between">
          <Icon type="fas fa-solid fa-circle" fontAwesome className="color-red-6 pt-8" />
        </div>
      )}
    </div>
  )
}

export default PremiumsQueue

const isPremiumIsLocked = (premiums: RecurringExpenseForReview[], recurringExpenseId: string): boolean => {
  return premiums.some((x) => x.recurringExpenseId === recurringExpenseId && x.lockedBy !== null)
}
